import { useNavigate } from "react-router-dom";
import { Sparkles, Hammer, Zap, ChevronDown, Check } from "lucide-react";

const IMG_GEN = "https://static.prod-images.emergentagent.com/jobs/2f7bdabb-c81c-4f41-9fe2-245fc40e5116/images/1aaaf6c434cb92313ff133b99a7afdb8a24fb626b3aff89f71f49f7a291cc1a2.jpeg";
const IMG_DIY = "https://static.prod-images.emergentagent.com/jobs/2f7bdabb-c81c-4f41-9fe2-245fc40e5116/images/6c41cef76afdff4487b2a68e08def212771bae9e9de86cd382aa3a3f0a08757f.jpeg";
const IMG_SOCIAL = "https://static.prod-images.emergentagent.com/jobs/2f7bdabb-c81c-4f41-9fe2-245fc40e5116/images/1fc4612ced2cfa9f0b1e9605a9545d928c58432201a462e4b9b22d910b9fe2d3.jpeg";

const Bullet = ({ children }) => (
  <li className="flex items-start gap-2 text-left">
    <Check size={14} className="mt-0.5 shrink-0 text-[#8fbf82]" />
    <span className="text-[12px] leading-snug text-white/70">{children}</span>
  </li>
);

// Rich "brochure" mode picker shown on the Home/Create page before a mode is chosen.
// Order: Generate (left) · Creator (middle) · Social (right) — matches the Plans page.
export default function CreatorModeBrochures({ onChooseGenerate }) {
  const navigate = useNavigate();
  const panel = "sms-tweed group relative flex flex-col overflow-hidden rounded-2xl bg-[#0f0f16] text-left transition-all duration-200 hover:-translate-y-1 focus:outline-none";
  const panelStyle = { border: "2px solid #5d8a52", boxShadow: "0 10px 26px -14px rgba(0,0,0,.7)" };

  return (
    <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6" data-testid="create-mode-brochures">
      {/* LEFT · CREATOR */}
      <button
        type="button"
        data-testid="brochure-diy"
        onClick={() => navigate("/diy")}
        className={`${panel} order-2`}
        style={panelStyle}
      >
        <span className="absolute top-2 left-2 z-10 text-[10px] font-extrabold text-white bg-[#f2711c] px-2 py-0.5 rounded-full">SAVE 25%</span>
        <div className="h-52 w-full overflow-hidden">
          <img src={IMG_DIY} alt="Homemade creator video at home" className="h-full w-full object-cover transition-transform duration-300 group-hover:scale-105" data-testid="brochure-diy-img" />
        </div>
        <div className="p-4 flex flex-col flex-1">
          <div className="flex items-center gap-2 mb-1">
            <Hammer size={18} className="text-[#f2711c]" />
            <span className="font-extrabold text-white text-base">Video Creator Mode</span>
          </div>
          <p className="text-[12px] text-white/60 mb-3">Make it truly yours. Bring your own photos, write your own script and record your own voice — perfect for a warm, homemade feel, like sharing your favorite recipe from your own kitchen.</p>
          <ul className="space-y-1.5 mb-4">
            <Bullet>Smart-Edit built in — upload your story, we handle the editing</Bullet>
            <Bullet>Use your real photos &amp; footage</Bullet>
            <Bullet>Your words, your voice, your story</Bullet>
            <Bullet>Authentic, personal &amp; the best value</Bullet>
          </ul>
          <span className="mt-auto inline-flex items-center justify-center gap-1.5 font-bold text-sm text-white py-2.5 rounded-xl" style={{ background: "#f2711c" }} data-testid="brochure-diy-cta">
            Build it yourself
          </span>
        </div>
      </button>

      {/* MIDDLE · GENERATE */}
      <button
        type="button"
        data-testid="brochure-generate"
        onClick={onChooseGenerate}
        className={`${panel} order-1`}
        style={{ ...panelStyle, borderColor: "#8ea2ff" }}
      >
        <span className="absolute top-2 left-2 z-10 text-[10px] font-extrabold text-white bg-[#1e3aaa] px-2 py-0.5 rounded-full">MOST POPULAR</span>
        <div className="h-52 w-full overflow-hidden">
          <img src={IMG_GEN} alt="AI-generated video thumbnails" className="h-full w-full object-cover transition-transform duration-300 group-hover:scale-105" data-testid="brochure-generate-img" />
        </div>
        <div className="p-4 flex flex-col flex-1">
          <div className="flex items-center gap-2 mb-1">
            <Sparkles size={18} className="text-[#8ea2ff]" />
            <span className="font-extrabold text-white text-base">Video Generate Mode</span>
          </div>
          <p className="text-[12px] text-white/60 mb-3">Just type your idea — our AI writes the script, generates the images, adds the voice-over and builds the whole video for you — Smart-Edit included, zero editing. The fastest way from idea to finished video.</p>
          <ul className="space-y-1.5 mb-4">
            <Bullet>AI images &amp; script from a single prompt</Bullet>
            <Bullet>Studio-quality voice-over included</Bullet>
            <Bullet>Chatbot Video Topic Expert add-on</Bullet>
            <Bullet>Ready-to-post in minutes, hands-free</Bullet>
          </ul>
          <span className="mt-auto inline-flex items-center justify-center gap-1.5 font-bold text-sm text-white py-2.5 rounded-xl" style={{ background: "linear-gradient(160deg,#2b46c9,#1e3aaa)" }} data-testid="brochure-generate-cta">
            Start below <ChevronDown size={15} />
          </span>
        </div>
      </button>

      {/* RIGHT · SOCIAL */}
      <button
        type="button"
        data-testid="brochure-social"
        onClick={() => navigate("/minute")}
        className={`${panel} order-3`}
        style={panelStyle}
      >
        <span className="absolute top-2 left-2 z-10 text-[10px] font-extrabold text-black bg-[#ffd21e] px-2 py-0.5 rounded-full">NEW</span>
        <div className="h-52 w-full overflow-hidden bg-black">
          <img src={IMG_SOCIAL} alt="Vertical portrait social videos" className="h-full w-full object-cover transition-transform duration-300 group-hover:scale-105" data-testid="brochure-social-img" />
        </div>
        <div className="p-4 flex flex-col flex-1">
          <div className="flex items-center gap-2 mb-1">
            <Zap size={18} className="text-[#ffd21e]" />
            <span className="font-extrabold text-white text-base">Social Video Mode</span>
          </div>
          <p className="text-[12px] text-white/60 mb-3">Built for Reels, Shorts &amp; TikTok. Create a punchy one-minute vertical clip in true portrait format with music and voice-over — designed to stop the scroll and get shared. Smart-Edit syncs and cuts it for you — no editing needed.</p>
          <ul className="space-y-1.5 mb-4">
            <Bullet>True 9:16 portrait, made to post</Bullet>
            <Bullet>Trending music &amp; rapid render</Bullet>
            <Bullet>Thumbnails that stop the scroll</Bullet>
          </ul>
          <span className="mt-auto inline-flex items-center justify-center gap-1.5 font-bold text-sm text-black py-2.5 rounded-xl" style={{ background: "#ffd21e" }} data-testid="brochure-social-cta">
            Go vertical
          </span>
        </div>
      </button>
    </div>
  );
}
